import type { PortalPillTone } from '@/components/portal/PortalStatusPill';
import { formatCalendarDate, formatDateTimeInTimeZone } from '@/lib/date-values';
import { formatDateOnly } from '@/lib/dates/date-only';

interface VisitPeriod {
  /** Date-only, inclusive. */
  firstDay: string;
  /** Null for a series' last period, which has no end. */
  lastDay: string | null;
}

const VISIT_PILL: Record<string, { label: string; tone: PortalPillTone }> = {
  // Booked while the office finishes planning it (a technician or a time): to
  // the customer it is scheduled.
  RESERVED: { label: 'Scheduled', tone: 'info' },
  SCHEDULED: { label: 'Scheduled', tone: 'info' },
  CONFIRMED: { label: 'Confirmed', tone: 'info' },
  DISPATCHED: { label: 'On the way', tone: 'accent' },
  IN_PROGRESS: { label: 'In progress', tone: 'accent' },
  // The technician paused the work on site (waiting on a part, say).
  ON_HOLD: { label: 'On hold', tone: 'accent' },
  CANCELLATION_PENDING: { label: 'Cancellation requested', tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'success' },
  // Visits that didn't happen stay in the history, so the customer sees why.
  SKIPPED: { label: 'Skipped', tone: 'neutral' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

const CLOSED_REASON_PILL: Record<'missed' | 'paused', { label: string; tone: PortalPillTone }> = {
  // Nobody was home: the customer didn't ask to skip it.
  missed: { label: 'Missed', tone: 'neutral' },
  // Cancelled while the plan was paused.
  paused: { label: 'Paused', tone: 'neutral' },
};

/**
 * The status pill of a portal visit — one map for Service history and the home
 * card, so the two never disagree. A booked visit with only a period has no
 * day yet.
 */
export function portalVisitPill(visit: {
  status: string;
  period: VisitPeriod | null;
  closedReason?: 'missed' | 'paused' | null;
}): {
  label: string;
  tone: PortalPillTone;
} {
  if (visit.status === 'RESERVED' && visit.period) {
    return { label: 'To be scheduled', tone: 'neutral' };
  }
  if (visit.closedReason) return CLOSED_REASON_PILL[visit.closedReason];
  return VISIT_PILL[visit.status] ?? { label: visit.status.toLowerCase(), tone: 'neutral' };
}

/** "Between Oct 5 and Nov 4" — the days a visit with no day yet falls within. */
export function formatVisitPeriod(period: VisitPeriod): string {
  const day = (value: string) => formatCalendarDate(value, { month: 'short', day: 'numeric' });
  return period.lastDay
    ? `Between ${day(period.firstDay)} and ${day(period.lastDay)}`
    : `From ${day(period.firstDay)}`;
}

/** When a portal visit happens, as the customer was promised it. */
export function portalVisitWhen(v: {
  startAt: string | null;
  scheduledDate: string | null;
  timezone: string;
  arrivalWindow: { start: string; end: string } | null;
  period: VisitPeriod | null;
}): string {
  // A visit promised as a window shows its day; the window line below says when
  // the technician arrives. The planned start inside it is the office's plan,
  // not something the customer was promised.
  if (v.arrivalWindow) {
    return formatDateTimeInTimeZone(v.arrivalWindow.start, v.timezone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  }
  if (v.startAt) {
    return formatDateTimeInTimeZone(v.startAt, v.timezone, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if (v.scheduledDate) return formatDateOnly(v.scheduledDate);
  if (v.period) return formatVisitPeriod(v.period);
  return 'To be scheduled';
}
